import React from "react";
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    Typography,
    Chip,
    CircularProgress,
} from "@mui/material";
import {
    CheckCircle as DoneIcon,
    ListAlt as BOMIcon,
    ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { format, parseISO } from "date-fns";

const STATUS_CHIPS = [
    { key: "in_stock", label: "In Stock", color: "success.main" },
    { key: "pulled", label: "Pulled", color: "info.main" },
    { key: "short", label: "Short", color: "warning.main" },
    { key: "order_req", label: "Order Req", color: "error.main" },
    { key: "on_order", label: "On Order", color: "warning.dark" },
    { key: "wo_req", label: "WO Req", color: "secondary.main" },
    { key: "pending", label: "Pending", color: "grey.400" },
];

function formatDate(dateStr) {
    if (!dateStr) return "—";
    try {
        return format(parseISO(dateStr), "MM/dd/yyyy");
    } catch {
        return dateStr;
    }
}

export default function BOMDashboardList({ rows, loading, onView }) {
    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (rows.length === 0) {
        return (
            <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                <BOMIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                <Typography variant="body2">No project BOMs found.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {rows.map((row) => (
                <Card
                    key={row.bom_id}
                    sx={{
                        border: 1,
                        borderColor: "primary.main",
                        // boxShadow: 1,
                        transition: "box-shadow 0.15s ease",
                        "&:hover": { boxShadow: 4 },
                    }}
                >
                    <CardActionArea
                        onClick={() => onView(row)}
                        sx={{ display: "flex", alignItems: "stretch" }}
                    >
                        <CardContent
                            sx={{
                                py: 1.5,
                                flex: 1,
                                minWidth: 0,
                                "&:last-child": { pb: 1.5 },
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    gap: 1,
                                }}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={700}
                                    >
                                        {row.project_number} ·{" "}
                                        {row.project_name}
                                        {row.bom_title ? ` — ${row.bom_title}` : ""}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Rev {row.bom_revision} · {row.total}{" "}
                                        line{row.total === 1 ? "" : "s"}
                                        {row.bom_status === "draft" ? " · Draft" : ""}
                                    </Typography>
                                </Box>
                                {row.all_pulled ? (
                                    <DoneIcon color="success" />
                                ) : row.needs_action > 0 ? (
                                    <Chip
                                        label={`${row.needs_action} need action`}
                                        size="small"
                                        color="error"
                                    />
                                ) : null}
                            </Box>

                            {row.total > 0 && (
                                <Box sx={{ mt: 1 }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            height: 8,
                                            borderRadius: 1,
                                            overflow: "hidden",
                                            bgcolor: "action.hover",
                                        }}
                                    >
                                        {STATUS_CHIPS.filter(
                                            (s) => row[s.key] > 0,
                                        ).map((s) => (
                                            <Box
                                                key={s.key}
                                                title={`${s.label}: ${row[s.key]}`}
                                                sx={{
                                                    width: `${(row[s.key] / row.total) * 100}%`,
                                                    bgcolor: s.color,
                                                }}
                                            />
                                        ))}
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: 1.25,
                                            mt: 0.5,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        {STATUS_CHIPS.filter(
                                            (s) => row[s.key] > 0,
                                        ).map((s) => (
                                            <Box
                                                key={s.key}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: "50%",
                                                        bgcolor: s.color,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {s.label}{" "}
                                                    <strong>
                                                        {row[s.key]}
                                                    </strong>
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{ mt: 0.75 }}
                            >
                                Updated {formatDate(row.last_updated)}
                            </Typography>
                        </CardContent>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                pr: 1,
                                flexShrink: 0,
                                color: "action.active",
                            }}
                        >
                            <ChevronRightIcon />
                        </Box>
                    </CardActionArea>
                </Card>
            ))}
        </Box>
    );
}
